import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FlaskConical, Plus, Archive, X, TrendingUp, Loader2, Trash2, Sparkles, RotateCcw, Pencil, Check, Ban, Undo2, ListChecks, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
const MODE_LABELS = { video: 'Video only', audio: 'Voice only', none: 'No media', free: 'Free' };
const MODE_COLORS = { video: 'text-sky-400', audio: 'text-emerald-400', none: 'text-slate-400', free: 'text-muted-foreground' };
const METRIC_LABELS = { scan_rate: 'scan rate', response_rate: 'response rate' };

function AbSignificance({ exp }) {
  const sig = exp.significance || { min_n: 25, results: [] };
  const below = (exp.variants || []).filter(v => (v.stats?.sent || 0) < sig.min_n);
  const significant = sig.results.filter(r => r.significant);
  const tested = sig.results.length > 0;

  return (
    <div className="mt-3 space-y-2" data-testid={`ab-significance-${exp.id}`}>
      {significant.map((r, i) => (
        <div key={i} data-testid="ab-sig-result"
          className="flex items-center gap-2 text-[11px] text-emerald-400 bg-emerald-950/20 border border-emerald-900/40 rounded-sm px-3 py-2">
          <TrendingUp className="w-3.5 h-3.5 shrink-0" />
          <span><span className="font-medium">"{r.winner}"</span> beats <span className="font-medium">"{r.loser}"</span> with {r.confidence}% confidence ({METRIC_LABELS[r.metric] || r.metric})</span>
        </div>
      ))}
      {tested && significant.length === 0 && (
        <p className="text-[11px] text-muted-foreground bg-secondary/30 border border-border/60 rounded-sm px-3 py-2" data-testid="ab-sig-none">
          No statistically significant difference yet (best signal: {Math.max(...sig.results.map(r => r.confidence)).toFixed(1)}% — needs ≥95%). Keep collecting data.
        </p>
      )}
      {below.length > 0 && (
        <div className="bg-secondary/20 border border-border/60 rounded-sm px-3 py-2.5" data-testid="ab-sig-progress">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Significance check unlocks at {sig.min_n} sent letters per variant
          </p>
          <div className="space-y-1.5">
            {(exp.variants || []).map(v => {
              const n = v.stats?.sent || 0;
              const p = Math.min(100, Math.round((n / sig.min_n) * 100));
              return (
                <div key={v.id} className="flex items-center gap-2" data-testid={`ab-sig-progress-${v.id}`}>
                  <span className="text-[10px] text-muted-foreground w-32 truncate">{v.name}</span>
                  <div className="flex-1 h-1.5 bg-border/40 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${n >= sig.min_n ? 'bg-emerald-500/70' : 'bg-violet-500/60'}`} style={{ width: `${p}%` }} />
                  </div>
                  <span className={`text-[10px] font-mono w-14 text-right ${n >= sig.min_n ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                    {n}/{sig.min_n}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-[9px] text-muted-foreground/60 leading-relaxed">
        A letter counts as "No response" {sig.window_days || 45} days after it was marked as Sent without a callback/booking; letters still within the window stay "Open".
        Response rate = responded ÷ sent letters. The significance tests need {sig.min_n} sent letters per variant.
      </p>
    </div>
  );
}

function ManagePagesDialog({ open, onOpenChange, experiments, reload }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getAbPages().then(r => setPages(r.data.pages || [])).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const variantName = (p) => {
    const exp = experiments.find(e => e.id === p.experiment_id);
    const v = exp?.variants?.find(x => x.id === p.variant_id);
    return v ? v.name : null;
  };

  const toggle = async (p) => {
    setBusy(p.id);
    try {
      const next = !p.ab_excluded;
      await api.setAbPageExcluded(p.id, next);
      setPages(prev => prev.map(x => x.id === p.id
        ? { ...x, ab_excluded: next, ...(next ? { experiment_id: null, variant_id: null } : {}) } : x));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setBusy(null);
  };

  const filtered = pages.filter(p => !q || (p.company_name || '').toLowerCase().includes(q.toLowerCase()));
  const excludedCount = pages.filter(p => p.ab_excluded).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ab-manage-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm inline-flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-violet-400" /> Letters & exclusions
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Exclude test deals from A/B testing — they are unassigned immediately and skipped by auto-assign, backfill and reset. Engagement data on the deal itself is kept.
          {excludedCount > 0 && <span className="text-red-400/80"> · {excludedCount} excluded</span>}
        </p>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-muted-foreground/50 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search company…" data-testid="ab-manage-search"
            className="w-full bg-secondary/40 border border-border rounded-sm pl-8 pr-3 py-2 text-xs text-foreground focus:outline-none focus:border-violet-700" />
        </div>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 text-violet-500 animate-spin mx-auto" /></div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-border/40 border border-border/60 rounded-sm">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">No letters found</p>
            )}
            {filtered.map(p => {
              const vn = variantName(p);
              return (
                <div key={p.id} className={`flex items-center gap-3 px-3 py-2 ${p.ab_excluded ? 'opacity-60' : ''}`}
                  data-testid={`ab-manage-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-foreground truncate">{p.company_name || '—'}</p>
                    <p className="text-[10px] text-muted-foreground/70 font-mono">
                      {(p.created_at || '').slice(0, 10)}{p.letter_status ? ` · ${p.letter_status}` : ''}{(p.visits || 0) > 0 ? ` · ${p.visits} scan${p.visits !== 1 ? 's' : ''}` : ''}
                    </p>
                  </div>
                  {p.ab_excluded ? (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-red-400 border-red-900/50 bg-red-950/30">excluded</span>
                  ) : vn ? (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-violet-400 border-violet-900/50 bg-violet-950/30">{vn}</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-slate-400 border-border bg-secondary/40">unassigned</span>
                  )}
                  <button onClick={() => toggle(p)} disabled={busy === p.id}
                    data-testid={`ab-manage-toggle-${p.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] border rounded-sm px-2 py-1 transition-colors disabled:opacity-50 shrink-0 ${p.ab_excluded
                      ? 'text-emerald-400 hover:text-emerald-300 border-emerald-900/40'
                      : 'text-red-400/80 hover:text-red-300 border-red-900/40'}`}>
                    {busy === p.id ? <Loader2 className="w-3 h-3 animate-spin" />
                      : p.ab_excluded ? <Undo2 className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                    {p.ab_excluded ? 'Include again' : 'Exclude'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const AbTestsSection = ({ experiments, unassigned, reload }) => {
  const { isAdmin } = useAuth();
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [variants, setVariants] = useState([{ name: '', media_mode: 'video' }, { name: '', media_mode: 'none' }]);
  const [targetN, setTargetN] = useState(50);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const [editingN, setEditingN] = useState(null);
  const [nDraft, setNDraft] = useState(50);
  const [showManage, setShowManage] = useState(false);

  const setVar = (i, patch) => setVariants(v => v.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.createAbExperiment({ name, variants: variants.filter(v => v.name.trim()), target_n: targetN });
      toast.success('Experiment created');
      setShowNew(false);
      setName('');
      setVariants([{ name: '', media_mode: 'video' }, { name: '', media_mode: 'none' }]);
      setTargetN(50);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create experiment'); }
    setSaving(false);
  };

  const handleQuickSetup = async () => {
    setQuickBusy(true);
    try {
      const res = await api.createAbExperiment({
        name: 'A/B Test: Video vs. Kein Video',
        variants: [
          { name: 'Mit Video', media_mode: 'video' },
          { name: 'Ohne Video', media_mode: 'none' },
        ],
        target_n: 50,
      });
      let assigned = 0;
      try {
        const bf = await api.backfillAbExperiment(res.data.id);
        assigned = bf.data.assigned || 0;
      } catch {}
      toast.success(`A/B test created${assigned > 0 ? ` — ${assigned} existing letter${assigned !== 1 ? 's' : ''} distributed across the 2 variants` : ''}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create A/B test'); }
    setQuickBusy(false);
  };

  const handleSwitchToAb = async () => {
    if (!window.confirm('Archive the current experiment and start the A/B test "Video vs. Kein Video"? ALL letters are redistributed onto the 2 new variants.')) return;
    setQuickBusy(true);
    try {
      const res = await api.switchToAbTest();
      const c = res.data.counts || {};
      toast.success(`A/B test started — ${res.data.assigned} letters redistributed (Mit Video: ${c['Mit Video'] ?? 0} · Ohne Video: ${c['Ohne Video'] ?? 0})`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Switch failed'); }
    setQuickBusy(false);
  };

  const handleBackfill = async (exp) => {
    setBackfilling(exp.id);
    try {
      const res = await api.backfillAbExperiment(exp.id);
      const { assigned, remaining_unassigned } = res.data;
      if (assigned === 0) toast.info('No unassigned letters to distribute');
      else toast.success(`${assigned} letter${assigned !== 1 ? 's' : ''} assigned${remaining_unassigned ? ` — ${remaining_unassigned} left over (all variants full)` : ''}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
    setBackfilling(null);
  };

  const handleArchive = async (exp) => {
    try {
      await api.updateAbExperiment(exp.id, { status: exp.status === 'active' ? 'archived' : 'active' });
      reload();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteExp = async (exp) => {
    if (!window.confirm(`Delete experiment "${exp.name}"? All letter assignments will be removed.`)) return;
    try {
      await api.deleteAbExperiment(exp.id);
      toast.success('Experiment deleted');
      reload();
    } catch { toast.error('Failed'); }
  };

  const handleReset = async (exp) => {
    if (!window.confirm(`Reset "${exp.name}"?\n\nALL letter assignments are removed and every counter starts from zero. Excluded letters stay excluded — afterwards use "Assign unassigned" to re-distribute the remaining (real) letters.`)) return;
    try {
      const res = await api.resetAbExperiment(exp.id);
      const n = res.data.removed || 0;
      toast.success(`Reset done — ${n} assignment${n !== 1 ? 's' : ''} removed. Exclude your test deals, then click "Assign unassigned".`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const handleSaveN = async (exp) => {
    const n = parseInt(nDraft, 10);
    if (!n || n < 1 || n > 1000) { toast.error('Target must be between 1 and 1000'); return; }
    try {
      await api.updateAbExperiment(exp.id, { target_n: n });
      toast.success(`Target set to n=${n} per variant`);
      setEditingN(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update target'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="ab-tests-section">
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-medium text-foreground inline-flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-400" /> Experiments & setup
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">Jeder Brief wird beim Schreiben aktiv einem Test zugeordnet (Auswahl auf der Outreach-Seite) — „Assign unassigned" verteilt Bestandsbriefe round-robin</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setShowManage(true)} data-testid="ab-manage-btn"
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-sm px-2.5 py-1.5 transition-colors">
              <ListChecks className="w-3 h-3" /> Letters & exclusions
            </button>
          )}
          {experiments.length > 0 && !experiments.some(x => x.status === 'active' && (x.variants || []).length === 2 && (x.variants || []).every(v => ['video', 'none'].includes(v.media_mode))) && (
            <button onClick={handleSwitchToAb} disabled={quickBusy} data-testid="ab-switch-to-ab-btn"
              className="inline-flex items-center gap-1.5 text-[11px] text-amber-400 hover:text-amber-300 border border-amber-900/40 rounded-sm px-2.5 py-1.5 transition-colors disabled:opacity-50">
              <Sparkles className="w-3 h-3" /> Switch to A/B (Video / Kein Video)
            </button>
          )}
          <button onClick={() => setShowNew(true)} data-testid="ab-new-experiment-btn"
            className="inline-flex items-center gap-1.5 text-[11px] text-violet-400 hover:text-violet-300 border border-violet-900/40 rounded-sm px-2.5 py-1.5 transition-colors">
            <Plus className="w-3 h-3" /> New experiment
          </button>
        </div>
      </div>
      {experiments.length === 0 ? (
        <div className="p-8 text-center" data-testid="ab-empty-hint">
          <FlaskConical className="w-8 h-8 text-violet-400/50 mx-auto mb-3" />
          <p className="text-sm text-foreground">No experiment yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Set up the standard A/B test — existing letters are distributed across the 2 variants immediately, and every new letter joins automatically (round-robin) until each variant reaches 50.
          </p>
          <Button onClick={handleQuickSetup} disabled={quickBusy} data-testid="ab-quick-setup-btn"
            className="mt-4 bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-9 px-4">
            {quickBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            Create A/B test (Video / Kein Video · n=50)
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          {experiments.map(exp => (
            <div key={exp.id} className="p-5" data-testid={`ab-exp-${exp.id}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs font-medium text-foreground">{exp.name}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${exp.status === 'active' ? 'text-emerald-400 border-emerald-900/50 bg-emerald-950/30' : 'text-slate-400 border-border bg-secondary/40'}`}>
                  {exp.status}
                </span>
                {exp.auto_assign !== false && exp.status === 'active' && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-violet-400 border-violet-900/50 bg-violet-950/30"
                    data-testid={`ab-autoassign-badge-${exp.id}`}>
                    auto-assign · n={exp.target_n || 50}/variant
                  </span>
                )}
                {editingN === exp.id ? (
                  <span className="inline-flex items-center gap-1">
                    <input type="number" min={1} max={1000} value={nDraft} autoFocus
                      onChange={e => setNDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveN(exp); if (e.key === 'Escape') setEditingN(null); }}
                      data-testid={`ab-edit-n-input-${exp.id}`}
                      className="w-16 bg-secondary/40 border border-violet-700 rounded-sm px-1.5 py-0.5 text-[11px] text-foreground focus:outline-none" />
                    <button onClick={() => handleSaveN(exp)} data-testid={`ab-edit-n-save-${exp.id}`}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingN(null)}
                      className="text-muted-foreground/60 hover:text-foreground transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ) : (
                  <button onClick={() => { setEditingN(exp.id); setNDraft(exp.target_n || 50); }}
                    title="Change target n per variant" data-testid={`ab-edit-n-${exp.id}`}
                    className="text-muted-foreground/40 hover:text-violet-400 transition-colors">
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
                {exp.created_by && <span className="text-[10px] text-muted-foreground/60">by {exp.created_by}</span>}
                <div className="ml-auto flex items-center gap-1.5">
                  {unassigned > 0 && (
                    <button onClick={() => handleBackfill(exp)} disabled={backfilling === exp.id}
                      data-testid={`ab-backfill-${exp.id}`}
                      className="inline-flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 border border-violet-900/40 rounded-sm px-2 py-1 transition-colors">
                      {backfilling === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Assign {unassigned} unassigned
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => handleReset(exp)} title="Reset — remove all assignments"
                      data-testid={`ab-reset-${exp.id}`}
                      className="text-muted-foreground/40 hover:text-orange-400 transition-colors">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => handleArchive(exp)} title={exp.status === 'active' ? 'Archive' : 'Reactivate'}
                    data-testid={`ab-archive-${exp.id}`}
                    className="text-muted-foreground/40 hover:text-amber-400 transition-colors">
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDeleteExp(exp)} title="Delete experiment"
                      data-testid={`ab-delete-${exp.id}`}
                      className="text-muted-foreground/40 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="py-2 pr-3 font-medium">Variant</th>
                      <th className="px-3 py-2 font-medium">Media</th>
                      <th className="px-3 py-2 font-medium text-right">Letters</th>
                      <th className="px-3 py-2 font-medium text-right">Sent</th>
                      <th className="px-3 py-2 font-medium text-right">Scanned</th>
                      <th className="px-3 py-2 font-medium text-right">Played</th>
                      <th className="px-3 py-2 font-medium text-right">Responded</th>
                      <th className="px-3 py-2 font-medium text-right">No resp.</th>
                      <th className="px-3 py-2 font-medium text-right">Open</th>
                      <th className="px-3 py-2 font-medium text-right">Scan rate</th>
                      <th className="px-3 py-2 font-medium text-right">Resp. rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {exp.variants.map(v => {
                      const s = v.stats || {};
                      return (
                        <tr key={v.id} data-testid={`ab-variant-row-${v.id}`}>
                          <td className="py-2 pr-3 text-xs font-medium text-foreground">{v.name}</td>
                          <td className={`px-3 py-2 text-[10px] ${MODE_COLORS[v.media_mode] || ''}`}>{MODE_LABELS[v.media_mode] || v.media_mode}</td>
                          <td className="px-3 py-2 text-xs font-mono text-right text-foreground" data-testid={`ab-counter-${v.id}`}>
                            {s.pages || 0}<span className="text-muted-foreground/50">/{exp.target_n || 50}</span>
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-right text-muted-foreground">{s.sent || 0}</td>
                          <td className={`px-3 py-2 text-xs font-mono text-right ${s.scanned > 0 ? 'text-emerald-400' : 'text-muted-foreground/50'}`}>{s.scanned || 0}</td>
                          <td className={`px-3 py-2 text-xs font-mono text-right ${s.played > 0 ? 'text-sky-400' : 'text-muted-foreground/50'}`}>{s.played || 0}</td>
                          <td className={`px-3 py-2 text-xs font-mono text-right ${s.responded > 0 ? 'text-amber-400' : 'text-muted-foreground/50'}`}>{s.responded || 0}</td>
                          <td className={`px-3 py-2 text-xs font-mono text-right ${s.no_response > 0 ? 'text-red-400/80' : 'text-muted-foreground/50'}`}>{s.no_response || 0}</td>
                          <td className="px-3 py-2 text-xs font-mono text-right text-muted-foreground">{s.open || 0}</td>
                          <td className="px-3 py-2 text-xs font-mono text-right text-muted-foreground">{pct(s.scanned || 0, s.sent || 0)}%</td>
                          <td className="px-3 py-2 text-xs font-mono text-right text-muted-foreground">{s.sent > 0 ? `${pct(s.responded || 0, s.sent)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <AbSignificance exp={exp} />
            </div>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="ab-new-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm inline-flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-violet-400" /> New A/B experiment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input value={name} onChange={e => setName(e.target.value)} placeholder='Name, e.g. "Video vs. voice Q3"'
              data-testid="ab-name-input"
              className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:border-violet-700" />
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Variants (min. 2)</span>
              {variants.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={v.name} onChange={e => setVar(i, { name: e.target.value })}
                    placeholder={`Variant ${String.fromCharCode(65 + i)}`} data-testid={`ab-variant-name-${i}`}
                    className="flex-1 bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:border-violet-700" />
                  <select value={v.media_mode} onChange={e => setVar(i, { media_mode: e.target.value })}
                    data-testid={`ab-variant-mode-${i}`}
                    className="bg-secondary/40 border border-border rounded-sm px-2 py-2 text-[11px] text-foreground focus:outline-none focus:border-violet-700">
                    <option value="video">Video only</option>
                    <option value="audio">Voice only</option>
                    <option value="none">No media</option>
                    <option value="free">Free (no enforcement)</option>
                  </select>
                  {variants.length > 2 && (
                    <button onClick={() => setVariants(vs => vs.filter((_, j) => j !== i))} data-testid={`ab-remove-variant-${i}`}
                      className="text-muted-foreground/40 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => setVariants(vs => [...vs, { name: '', media_mode: 'none' }])} data-testid="ab-add-variant"
                className="text-[11px] text-violet-400 hover:text-violet-300 transition-colors inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add variant
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Target per variant</span>
              <input type="number" min={1} max={1000} value={targetN}
                onChange={e => setTargetN(parseInt(e.target.value) || 50)} data-testid="ab-target-n-input"
                className="w-20 bg-secondary/40 border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:border-violet-700" />
              <span className="text-[10px] text-muted-foreground">letters — new letters are distributed round-robin until each variant is full</span>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Media enforcement on the public page: "Video only" hides the voice player, "Voice only" hides the video, "No media" shows photo + text + booking only — regardless of what is uploaded. "Free" leaves the page as is.
            </p>
            <Button onClick={handleCreate}
              disabled={saving || !name.trim() || variants.filter(v => v.name.trim()).length < 2}
              data-testid="ab-create-btn"
              className="w-full bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create experiment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManagePagesDialog open={showManage} onOpenChange={setShowManage} experiments={experiments} reload={reload} />
    </div>
  );
};
